import { useQuery } from '@tanstack/react-query';
import { DeferredOverlay } from '../../components/DeferredOverlay';
import {
  RAIL_ITEM_IDLE_BG,
  RAIL_ITEM_IDLE_TEXT,
  RAIL_ITEM_OPEN_BG,
  RAIL_ITEM_OPEN_TEXT,
  railItem,
  railItemFill,
  railLabel,
} from '../../components/railItem';
import { useRailCollapsed } from '../../lib/railCollapse';
import { portfolioScopeLabel } from '../portfolio/PortfolioScopeOptions';
import { usePortfolioScope } from '../portfolio/scope';
import { useSession } from './session';
import { trpc } from '../../lib/trpc';

/**
 * Who you are, which organisation you are in, and how to leave.
 *
 * The organisation is stated in plain words rather than implied by a logo, and a support
 * session says so in colour. Someone from ProManage working inside a customer's books must
 * never be one glance away from thinking they are in their own — that mistake is how a
 * support call becomes an incident report.
 */
export function AccountMenu() {
  const session = useSession();
  const photo = useQuery(
    trpc.auth.profilePhoto.queryOptions(undefined, { staleTime: 60_000, enabled: !!session }),
  );
  const [collapsed] = useRailCollapsed();
  const { scope, canToggle: canChooseScope } = usePortfolioScope();

  // Only for the avatar's tint below — the panel reads the same cached query for the rest.
  const orgs = useQuery(trpc.auth.organisations.queryOptions(undefined, { staleTime: 60_000 }));
  const current = orgs.data?.find((org) => org.isCurrent);

  if (!session) return null;

  const initial = session.user.name.trim().charAt(0).toUpperCase() || '?';

  // Only for somebody who has the choice. Stating "My portfolio" under the name of every
  // property manager in the agency would label the default, which tells a reader nothing and
  // makes the one line that matters harder to notice.
  const showing = canChooseScope ? portfolioScopeLabel(scope) : null;

  return (
    <DeferredOverlay
      overlay="AccountPopover"
      failureTitle="Could not open your account menu"
      triggerProps={{
        className: `${railItem(collapsed, true)} ${RAIL_ITEM_IDLE_TEXT} ${RAIL_ITEM_OPEN_TEXT}`,
        // The scope goes in the accessible name as well as under it, because the second line is
        // the first thing the rail drops when it collapses — and to a screen reader the row is
        // one control either way.
        'aria-label': showing
          ? `Account: ${session.user.name}. Showing: ${showing}`
          : `Account: ${session.user.name}`,
        title: collapsed && showing ? `${session.user.name} — ${showing}` : undefined,
      }}
    >
      <span
        aria-hidden="true"
        className={`${railItemFill(collapsed)} ${RAIL_ITEM_IDLE_BG} ${RAIL_ITEM_OPEN_BG}`}
      />
      <span
        className={`relative flex size-7 shrink-0 items-center justify-center rounded-sm text-[0.8125rem] font-bold ${
          current?.isSupport ? 'bg-warn-soft text-warn' : 'bg-accent-soft text-accent'
        }`}
      >
        {photo.data ? (
          <img src={photo.data} alt="" className="size-full rounded-sm object-cover" />
        ) : (
          initial
        )}
      </span>
      {/*
       * Which portfolio, under the name, in the one place that is always on screen.
       *
       * The picker itself is a click away inside the menu, and a control you have to open to
       * learn the state of is no use to somebody wondering why a total looks small. This is
       * the standing answer; the menu is where it gets changed.
       */}
      <span className={railLabel(collapsed, true, true)}>
        {/* Both lines carry their own leading. The row is a fixed 40px shared with every
              other rail control, and two lines at the inherited body line-height do not fit
              inside it — they push the fill out of alignment with the rows above. */}
        <span className="block truncate leading-tight">{session.user.name}</span>
        {showing ? (
          <span className="block truncate text-[0.6875rem] leading-tight font-normal text-ink-subtle">
            {showing}
          </span>
        ) : null}
      </span>
    </DeferredOverlay>
  );
}
